import { lazy, Suspense, useEffect, useId, useRef } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import mermaid from "mermaid";
import DOMPurify from "dompurify";
import { cn } from "@/lib/utils";

mermaid.initialize({ startOnLoad: false, theme: "default", suppressErrorRendering: true });

// Lazy load syntax highlighter to reduce initial bundle size
const SyntaxHighlighter = lazy(() =>
  import("react-syntax-highlighter").then((mod) => ({ default: mod.Prism }))
);

// Loading fallback for code blocks
function CodeBlockFallback() {
  return (
    <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg animate-pulse">
      <code className="text-sm">Loading code...</code>
    </pre>
  );
}

function MermaidBlock({ code }: { code: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const id = useId().replace(/:/g, "m");

  useEffect(() => {
    if (!ref.current) return;
    let cancelled = false;
    mermaid
      .render(`mermaid-${id}`, code)
      .then(({ svg }: { svg: string }) => {
        if (!cancelled && ref.current)
          ref.current.innerHTML = DOMPurify.sanitize(svg, {
            ADD_TAGS: ["foreignObject"],
          });
      })
      .catch(() => {
        if (!cancelled && ref.current) ref.current.textContent = code;
      });
    return () => { cancelled = true; };
  }, [code, id]);

  return <div ref={ref} className="my-4 flex justify-center" />;
}

interface MarkdownContentProps {
  children: string;
  className?: string;
}

/**
 * Shared markdown renderer with proper code block handling.
 *
 * Correctly distinguishes inline code from code blocks by checking for newlines,
 * since react-markdown v9+ removed the `inline` prop and languageless code blocks
 * have undefined className (same as inline code).
 */
// Module-scope component map. Stable identity across all renders of any
// MarkdownContent instance, so ReactMarkdown reconciles children in place
// instead of treating each render as a fresh set of component definitions.
//
// CRITICAL: declaring this inline inside the component (as `components={{...}}`
// in JSX) creates new function references every render — ReactMarkdown then
// remounts every matching element (headings, code blocks, inline code). When
// SyntaxHighlighter is involved, the Suspense fallback flashes briefly between
// remounts, the document height collapses, and the browser clamps scrollY to
// fit, teleporting the viewport to the top of the page.
const MARKDOWN_COMPONENTS: Components = {
  // Heading level shift: demote all headings by 2 levels
  // so markdown authored with # and ## renders proportionally
  // within embedded content (panels, question text, feedback)
  h1: ({ children }) => <h2>{children}</h2>,
  h2: ({ children }) => <h3>{children}</h3>,
  h3: ({ children }) => <h4>{children}</h4>,
  h4: ({ children }) => <h5>{children}</h5>,
  code({ className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || "");
    const codeContent = String(children).replace(/\n$/, "");
    // Code blocks contain newlines; inline code doesn't
    const isInline = !match && !codeContent.includes("\n");

    if (isInline) {
      return (
        <code
          className="bg-muted px-1.5 py-0.5 rounded font-mono"
          {...props}
        >
          {children}
        </code>
      );
    }

    // Mermaid diagrams — render as SVG
    const language = match ? match[1] : "text";
    if (language === "mermaid") {
      return (
        <div className="not-prose">
          <MermaidBlock code={codeContent} />
        </div>
      );
    }

    // Block code with syntax highlighting (wrapped in not-prose to escape typography styles)
    return (
      <div className="not-prose">
        <Suspense fallback={<CodeBlockFallback />}>
          <SyntaxHighlighter
            language={language}
            PreTag="div"
            className="rounded-lg !my-4"
            useInlineStyles={true}
          >
            {codeContent}
          </SyntaxHighlighter>
        </Suspense>
      </div>
    );
  },
  // Prevent prose from wrapping code blocks in styled <pre>
  pre: ({ children }) => <>{children}</>,
};

// Stable plugins reference, same reasoning as MARKDOWN_COMPONENTS.
const MARKDOWN_PLUGINS = [remarkGfm];

export function MarkdownContent({ children, className }: MarkdownContentProps) {
  return (
    <div
      className={cn(
        "prose dark:prose-invert max-w-none",
        // Body text: inherit app base (16px/1.5 from body)
        // Headings: shifted down 2 levels, semibold (600) not bold (700)
        "prose-headings:font-semibold",
        "prose-h1:text-3xl prose-h1:leading-[1.25]",  // md h1 → visual H2 (30px)
        "prose-h2:text-2xl prose-h2:leading-[1.3]",   // md h2 → visual H3 (24px)
        "prose-h3:text-xl prose-h3:leading-[1.35]",   // md h3 → visual H4 (20px)
        // Lists: tight spacing
        "prose-ul:list-disc prose-ol:list-decimal prose-li:my-1",
        // Strong: semibold (600) not bold (700)
        "prose-strong:font-semibold",
        // Code: no backtick pseudo-elements
        "prose-code:before:content-none prose-code:after:content-none",
        className
      )}
    >
      <ReactMarkdown
        skipHtml={true}
        remarkPlugins={MARKDOWN_PLUGINS}
        components={MARKDOWN_COMPONENTS}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
